/* eslint-disable react/no-unknown-property */
import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, Grid, Text } from "@react-three/drei";
import * as THREE from "three";

const g = 9.81;

const PendulumBody = ({ L, theta0 }) => {
  const groupRef = useRef();
  const bobRef = useRef();
  const stringRef = useRef();
  const omega = Math.sqrt(g / L);
  const amp = (theta0 * Math.PI) / 180;

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const theta = amp * Math.cos(omega * t);
    if (groupRef.current) groupRef.current.rotation.z = theta;
  });

  return (
    <group position={[0, 3, 0]} ref={groupRef}>
      {/* String */}
      <mesh position={[0, -L / 2, 0]} ref={stringRef}>
        <cylinderGeometry args={[0.015, 0.015, L, 8]} />
        <meshStandardMaterial color="#e5e7eb" />
      </mesh>
      {/* Bob */}
      <mesh position={[0, -L, 0]} ref={bobRef} castShadow>
        <sphereGeometry args={[0.28, 32, 32]} />
        <meshStandardMaterial color="#22d3ee" metalness={0.6} roughness={0.15} emissive="#0891b2" emissiveIntensity={0.25} />
      </mesh>
    </group>
  );
};

const Stand = () => (
  <group>
    {/* Base */}
    <mesh position={[0, 0.05, 0]} receiveShadow>
      <boxGeometry args={[3, 0.1, 1.5]} />
      <meshStandardMaterial color="#334155" metalness={0.5} roughness={0.5} />
    </mesh>
    {/* Vertical rod */}
    <mesh position={[-1.2, 1.55, 0]}>
      <cylinderGeometry args={[0.05, 0.05, 3, 12]} />
      <meshStandardMaterial color="#64748b" metalness={0.7} roughness={0.3} />
    </mesh>
    {/* Horizontal rod */}
    <mesh position={[-0.6, 3, 0]} rotation={[0, 0, Math.PI / 2]}>
      <cylinderGeometry args={[0.04, 0.04, 1.2, 12]} />
      <meshStandardMaterial color="#64748b" metalness={0.7} roughness={0.3} />
    </mesh>
    {/* Pivot */}
    <mesh position={[0, 3, 0]}>
      <sphereGeometry args={[0.06, 16, 16]} />
      <meshStandardMaterial color="#f59e0b" metalness={0.8} roughness={0.2} />
    </mesh>
  </group>
);

const Pendulum3D = ({ params }) => {
  const L = +params.L || 1;
  const theta0 = +params.theta0 || 15;
  const T = useMemo(() => 2 * Math.PI * Math.sqrt(L / g), [L]);

  return (
    <Canvas shadows camera={{ position: [3, 3, 5], fov: 45 }} dpr={[1, 2]}>
      <color attach="background" args={["#020617"]} />
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 8, 3]} intensity={1.2} castShadow shadow-mapSize={[1024, 1024]} />
      <pointLight position={[-3, 4, -3]} intensity={0.5} color="#22d3ee" />

      <Stand />
      <PendulumBody L={L} theta0={theta0} />

      <Grid args={[10, 10]} cellColor="#1e293b" sectionColor="#0891b2" position={[0, 0.11, 0]} fadeDistance={12} />

      <Text position={[2, 3.5, 0]} fontSize={0.18} color="#22d3ee" anchorX="left">
        {`L = ${L.toFixed(2)} m`}
      </Text>
      <Text position={[2, 3.2, 0]} fontSize={0.18} color="#22d3ee" anchorX="left">
        {`θ₀ = ${theta0}°`}
      </Text>
      <Text position={[2, 2.9, 0]} fontSize={0.18} color="#f59e0b" anchorX="left">
        {`T = ${T.toFixed(3)} s`}
      </Text>

      <Environment preset="city" />
      <OrbitControls enablePan={false} minDistance={3} maxDistance={12} maxPolarAngle={Math.PI / 2 - 0.05} />
    </Canvas>
  );
};

export default Pendulum3D;
